import React from 'react';
import RapperInput from '../RapperInput';
import { getConfidenceColor } from '../../utils/battleTitleParser';

const VideoRow = ({
  video,
  isSelected,
  form,
  onSelectVideo,
  onRapperChange,
  onRapperSelect,
  onAddRapper,
  onRemoveRapper,
  onEventChange,
  onAutoImport,
  parsing,
}) => {
  return (
    <tr
      className={`border-b border-gray-700 hover:bg-gray-800 transition ${
        isSelected ? 'bg-gray-800' : ''
      }`}
    >
      {/* Checkbox */}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelectVideo(video)}
          className="w-5 h-5 cursor-pointer"
        />
      </td>

      {/* Video Info */}
      <td className="px-4 py-3">
        <div className="flex items-start gap-3">
          <a
            href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="relative group flex-shrink-0"
          >
            <img
              src={video.thumbnail_url}
              alt={video.title}
              className="w-32 h-20 object-cover rounded"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded">
              <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </a>
          <div className="flex-1">
            <div className="text-white font-medium text-sm line-clamp-2">
              {video.title}
            </div>
            <div className="text-gray-400 text-xs mt-1">
              {new Date(video.published_at).toLocaleDateString()} •{' '}
              {video.view_count?.toLocaleString() || 0} views
            </div>
            {!isSelected && (
              <button
                onClick={() => onAutoImport(video)}
                disabled={parsing}
                className="mt-2 px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs rounded flex items-center gap-1"
                title="Use AI to parse rapper names, event, and format from title"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Parse
              </button>
            )}
          </div>
        </div>
      </td>

      {/* Rapper Inputs */}
      {isSelected && form ? (
        <>
          <td className="px-4 py-3">
            <div className="relative">
              <RapperInput
                value={form.rappers[0]?.name || ''}
                existingId={form.rappers[0]?.existing_id}
                onChange={(value) => onRapperChange(video.youtube_id, 0, value)}
                onSelect={(existingId, displayName) =>
                  onRapperSelect(video.youtube_id, 0, existingId, displayName)
                }
                placeholder="Rapper 1 *"
                index={0}
                canRemove={false}
              />
              {form.rappers[0]?.confidence !== undefined && (
                <div className={`text-xs mt-1 ${getConfidenceColor(form.rappers[0].confidence)}`}>
                  {form.rappers[0].isExactMatch ? '✓ Exact match' : `${form.rappers[0].confidence}% match`}
                </div>
              )}
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="relative">
              <RapperInput
                value={form.rappers[1]?.name || ''}
                existingId={form.rappers[1]?.existing_id}
                onChange={(value) => onRapperChange(video.youtube_id, 1, value)}
                onSelect={(existingId, displayName) =>
                  onRapperSelect(video.youtube_id, 1, existingId, displayName)
                }
                placeholder="Rapper 2 *"
                index={1}
                canRemove={false}
              />
              {form.rappers[1]?.confidence !== undefined && (
                <div className={`text-xs mt-1 ${getConfidenceColor(form.rappers[1].confidence)}`}>
                  {form.rappers[1].isExactMatch ? '✓ Exact match' : `${form.rappers[1].confidence}% match`}
                </div>
              )}
            </div>
          </td>
          <td className="px-4 py-3">
            {form.rappers.length > 2 ? (
              <div className="space-y-2">
                {form.rappers.slice(2).map((rapper, idx) => (
                  <RapperInput
                    key={idx + 2}
                    value={rapper.name}
                    existingId={rapper.existing_id}
                    onChange={(value) =>
                      onRapperChange(video.youtube_id, idx + 2, value)
                    }
                    onSelect={(existingId, displayName) =>
                      onRapperSelect(video.youtube_id, idx + 2, existingId, displayName)
                    }
                    onRemove={() => onRemoveRapper(video.youtube_id, idx + 2)}
                    placeholder={`Rapper ${idx + 3}`}
                    index={idx + 2}
                    canRemove={true}
                  />
                ))}
              </div>
            ) : null}
            <button
              onClick={() => onAddRapper(video.youtube_id)}
              className="mt-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
            >
              + Add Rapper
            </button>
          </td>
          <td className="px-4 py-3">
            <input
              type="text"
              value={form.event_name}
              onChange={(e) => onEventChange(video.youtube_id, e.target.value)}
              placeholder="Event Name (optional)"
              className="w-full px-3 py-2 bg-battle-dark border border-army/40 rounded text-white text-sm focus:outline-none focus:border-brand-yellow/60"
            />
            {form?.parsedByAI && (
              <div className="text-xs text-gray-400 mt-1">
                <span className={`${form.parseConfidence >= 80 ? 'text-green-500' : form.parseConfidence >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                  AI: {form.parseConfidence}% confident
                </span>
                {form.parseReasoning && <span> — {form.parseReasoning}</span>}
              </div>
            )}
          </td>
        </>
      ) : (
        <>
          <td
            colSpan="4"
            className="px-4 py-3 text-center text-gray-500 text-sm"
          >
            Select video to edit
          </td>
        </>
      )}
    </tr>
  );
};

export default VideoRow;
